import React, {useState, useEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {hooks} from '../hooks';
import {custom} from '../custom';
import {theme} from '../constants';
import {components} from '../components';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { getCurrentLocation } from '../utils/locationUtils';
import { MdTitle, MdBusiness, MdSubject, MdDescription, MdLocationOn, MdCalendarMonth, MdCheckCircle, MdError, MdWarning, MdArrowDropDown } from 'react-icons/md';

const TitleIcon = MdTitle as any;
const BusinessIcon = MdBusiness as any;
const SubjectIcon = MdSubject as any;
const DescriptionIcon = MdDescription as any;
const LocationIcon = MdLocationOn as any;
const CalendarIcon = MdCalendarMonth as any;
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const ArrowIcon = MdArrowDropDown as any;

export const CreateMarketingActivity: React.FC = () => {
  const {pathname} = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();

  const getDefaultDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 16);
  };

  const [datetime, setDatetime] = useState<string>(getDefaultDateTime());
  const [activityType, setActivityType] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [locationError, setLocationError] = useState<string>('');
  const [isFetchingLocation, setIsFetchingLocation] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [activityTypesList, setActivityTypesList] = useState<any[]>([]);

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: -1, left: 0, behavior: 'smooth'});
    }, 10);
  }, [pathname]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const locationData = await getCurrentLocation({ addressFormat: 'full' });
        setLocation(`${locationData.lat},${locationData.lng}`);
        setAddress(locationData.address || t.attendance?.addressNotFound || 'Address not found');
      } catch (error: any) {
        setLocationError(error.message || 'Failed to get location. Please enable GPS and allow permission.');
      } finally {
        setIsFetchingLocation(false);
      }
    };

    fetchLocation();
  }, []);

  useEffect(() => {
    const fetchActivityTypes = async () => {
      try {
        const response = await axiosInstance.get('/marketing-activities/types');
        setActivityTypesList(response.data);
      } catch (error) {
        console.error('Failed to fetch activity types:', error);
      }
    };

    fetchActivityTypes();
  }, []);

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    if (onClose) onClose();
  };

  const handleCreate = async () => {
    if (!datetime || !activityType || !companyName || !subject) {
      showAlert(t.common?.warning || "Warning", t.marketingActivity?.validation?.fillRequired || "Date, Activity Type, Company Name, and Subject are required.", 'warning');
      return;
    }

    if (isFetchingLocation) {
      showAlert(t.common?.warning || "Warning", t.marketingActivity?.waitingLocation || "Harap tunggu, sedang mendapatkan koordinat lokasi...", 'warning');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        datetime: new Date(datetime).toISOString(),
        activity_type: activityType,
        company_name: companyName,
        subject,
        description,
        coordinates: location,
        address
      };

      await axiosInstance.post('/marketing-activities', payload);
      
      showAlert(
        t.common?.success || "Success", 
        t.marketingActivity?.alerts?.success || "Marketing activity created successfully!", 
        'success', 
        () => {
          navigate('/MarketingActivity', { replace: true });
        }
      );
    } catch (error) {
      console.error('Failed to create marketing activity:', error);
      showAlert(t.common?.error || "Error", t.marketingActivity?.alerts?.failed || "Failed to create activity. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderBackground = (): JSX.Element => <components.Background />;

  const renderHeader = (): JSX.Element => (
    <components.Header title={t.marketingActivity?.createTitle || "Create Marketing Activity"} goBack={true}  />
  );

  const renderContent = (): JSX.Element => {
    return (
      <main style={{marginTop: 62, paddingBottom: 100}} className='container'>
        
        {/* Card 1: Activity Details */}
        <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 16, border: '1px solid #F3F4F6', marginBottom: 20 }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20}}>
            <div style={{width: 4, height: 16, backgroundColor: theme.colors.mainColor, borderRadius: 4}} />
            <text.H5 style={{color: theme.colors.mainDark}}>{t.marketingActivity?.activityInfo || 'Activity Info'}</text.H5>
          </div>
          
          <div style={{marginBottom: 16}}>
            <text.T14 style={{color: '#6B7280', marginBottom: 6, display: 'block'}}>{t.marketingActivity?.dateAndTime || 'Date & Time'} <span style={{color: '#EF4444'}}>*</span></text.T14>
            <custom.InputField placeholder="Select Date and Time" value={datetime} onChange={(e: any) => setDatetime(e.target.value)} leftIcon={<CalendarIcon size={20} color="#9CA3AF" />} type={"datetime-local" as any} containerStyle={{marginBottom: 0}} />
          </div>

          <div style={{marginBottom: 16}}>
            <text.T14 style={{color: '#6B7280', marginBottom: 6, display: 'block'}}>{t.marketingActivity?.type || 'Activity Type'} <span style={{color: '#EF4444'}}>*</span></text.T14>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', paddingLeft: 14, top: '50%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none' }}><TitleIcon size={20} color={'#9CA3AF'} /></div>
              <select value={activityType} onChange={(e) => setActivityType(e.target.value)} style={{ width: '100%', height: 50, paddingLeft: 44, paddingRight: 20, borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', appearance: 'none', fontSize: 14, color: theme.colors.mainDark, ...theme.fonts.SourceSansPro_400Regular, }}>
                <option value='' disabled hidden>{t.marketingActivity?.selectType || 'Select Activity Type'}</option>
              {activityTypesList.map((typeObj, idx) => (
                <option key={idx} value={typeObj.type}>{typeObj.type}</option>
              ))}
              </select>
              <div style={{position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none'}}><ArrowIcon size={24} color={'#9CA3AF'} /></div>
            </div>
          </div>

          <div style={{marginBottom: 16}}>
            <text.T14 style={{color: '#6B7280', marginBottom: 6, display: 'block'}}>{t.marketingActivity?.companyName || 'Company Name'} <span style={{color: '#EF4444'}}>*</span></text.T14>
            <custom.InputField placeholder={t.marketingActivity?.enterCompany || "Enter company name"} value={companyName} onChange={(e: any) => setCompanyName(e.target.value)} leftIcon={<BusinessIcon size={20} color="#9CA3AF" />} containerStyle={{marginBottom: 0}} />
          </div>

          <div style={{marginBottom: 0}}>
            <text.T14 style={{color: '#6B7280', marginBottom: 6, display: 'block'}}>{t.marketingActivity?.subject || 'Subject'} <span style={{color: '#EF4444'}}>*</span></text.T14>
            <custom.InputField placeholder={t.marketingActivity?.enterSubject || "Enter subject"} value={subject} onChange={(e: any) => setSubject(e.target.value)} leftIcon={<SubjectIcon size={20} color="#9CA3AF" />} containerStyle={{marginBottom: 0}} />
          </div>
        </div>

        {/* Card 2: Additional Notes */}
        <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 16, border: '1px solid #F3F4F6', marginBottom: 20 }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16}}>
            <div style={{width: 4, height: 16, backgroundColor: '#F59E0B', borderRadius: 4}} />
            <text.H5 style={{color: theme.colors.mainDark}}>{t.marketingActivity?.description || 'Description'}</text.H5>
          </div>
          <div style={{ width: '100%', padding: '14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12 }}>
            <textarea placeholder={t.marketingActivity?.descriptionPlaceholder || "Write a detailed description..."} value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: '100%', height: 120, ...theme.fonts.SourceSansPro_400Regular, outline: 'none', border: 'none', resize: 'none', backgroundColor: 'transparent', fontSize: 14 }} />
          </div>
        </div>
        
        {/* Card 3: Location */}
        <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 16, border: '1px solid #F3F4F6', marginBottom: 20 }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16}}>
            <div style={{width: 4, height: 16, backgroundColor: '#10B981', borderRadius: 4}} />
            <text.H5 style={{color: theme.colors.mainDark}}>{t.marketingActivity?.location || 'Location'}</text.H5>
          </div>
          <div style={{ padding: 16, backgroundColor: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ marginTop: 2 }}><LocationIcon size={24} color="#10B981" /></div>
            <div style={{flex: 1}}>
              {isFetchingLocation ? (<text.T14 style={{color: '#9CA3AF', fontStyle: 'italic'}}>{t.common?.loading || 'Fetching location...'}</text.T14>) : locationError ? (<text.T14 style={{color: '#EF4444'}}>{locationError}</text.T14>) : (<><text.T14 style={{color: theme.colors.mainDark, lineHeight: 1.5, fontWeight: '500', display: 'block'}}>{address || 'Location acquired'}</text.T14><text.T14 style={{color: '#6B7280', fontSize: 12, marginTop: 6, display: 'block'}}>{location}</text.T14></>)}
            </div>
          </div>
        </div>
      </main>
    );
  };

  const renderButton = (): JSX.Element => (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: 'white', padding: '16px 24px 32px', boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 10 }}>
      <div className='container' style={{padding: 0, maxWidth: 650, margin: '0 auto'}}><components.Button title={loading ? (t.common?.saving || "Saving...") : (t.marketingActivity?.submit || "Submit Activity")} onClick={handleCreate} /></div>
    </div>
  );

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{marginBottom: 16}}>{alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}{alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}{alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}</div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>{alertConfig.message}</text.T16>
          <components.Button title={t.common?.ok || "OK"} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  return (
    <div id='screen'>
      {renderBackground()}
      {renderHeader()}
      <div style={{padding: 0}}>{renderContent()}</div>
      {renderButton()}
      {renderAlert()}
    </div>
  );
};